/* eslint-disable @typescript-eslint/no-unsafe-call */
import type { QueryDslQueryContainer } from '@elastic/elasticsearch/lib/api/types';
import type { ElasticsearchQuery } from 'searchkit';

import type { Langcodes } from '@uncinc/react-drupal-core/types';

import type {
  MetaParagraphsContentTypes,
  ParagraphContentOverview,
  ParagraphContentSelection,
} from '../../../types/uncinc_profile/paragraphs';
import type { SearchAllContentHit } from '../../SearchAllContent/types';

function constructAdvancedFilters(
  queryFilters: QueryDslQueryContainer,
  paragraph: ParagraphContentSelection | ParagraphContentOverview,
): QueryDslQueryContainer {
  const filters = queryFilters;
  const contentType = paragraph.field_content_type[0].value;

  if (contentType === 'article') {
    paragraph.field_article_types.forEach((articleType) => {
      // @ts-ignore
      filters.bool?.should?.push({
        term: {
          article_type_tid: {
            value: articleType.target_id,
          },
        },
      });
    });

    if ('field_themes' in paragraph) {
      paragraph.field_themes.forEach((theme) => {
        // @ts-ignore
        filters.bool?.should?.push({
          term: {
            themes_tid: {
              value: theme.target_id,
            },
          },
        });
      });
    }
  }

  if (contentType === 'project') {
    paragraph.field_project_types.forEach((projectType) => {
      // @ts-ignore
      filters.bool?.should?.push({
        term: {
          project_types_tid: {
            value: projectType.target_id,
          },
        },
      });
    });

    paragraph.field_project_statuses.forEach((projectStatus) => {
      // @ts-ignore
      filters.bool?.should?.push({
        term: {
          project_status_tid: {
            value: projectStatus.target_id,
          },
        },
      });
    });

    if ('field_themes' in paragraph) {
      paragraph.field_themes.forEach((theme) => {
        // @ts-ignore
        filters.bool?.should?.push({
          term: {
            themes_tid: {
              value: theme.target_id,
            },
          },
        });
      });
    }

    if ('field_departments' in paragraph) {
      paragraph.field_departments.forEach((department) => {
        // @ts-ignore
        filters.bool?.should?.push({
          term: {
            departments_tid: {
              value: department.target_id,
            },
          },
        });
      });
    }
  }

  if (contentType === 'vacancy') {
    if ('field_departments' in paragraph) {
      paragraph.field_departments.forEach((department) => {
        // @ts-ignore
        filters.bool?.should?.push({
          term: {
            departments_tid: {
              value: department.target_id,
            },
          },
        });
      });
    }

    if ('field_locations' in paragraph) {
      paragraph.field_locations.forEach((location) => {
        // @ts-ignore
        filters.bool?.should?.push({
          term: {
            vacancy_locations_tid: {
              value: location.target_id,
            },
          },
        });
      });
    }
  }

  if (contentType === 'profile') {
    if ('field_departments' in paragraph) {
      paragraph.field_departments.forEach((department) => {
        // @ts-ignore
        filters.bool?.should?.push({
          term: {
            profile_departments_tid: {
              value: department.target_id,
            },
          },
        });
      });
    }

    if ('field_locations' in paragraph) {
      paragraph.field_locations.forEach((location) => {
        // @ts-ignore
        filters.bool?.should?.push({
          term: {
            profile_locations_tid: {
              value: location.target_id,
            },
          },
        });
      });
    }
  }

  if (contentType === 'equipment') {
    paragraph.field_equipment_types.forEach((equipmentType) => {
      // @ts-ignore
      filters.bool?.should?.push({
        term: {
          equipment_types_tid: {
            value: equipmentType.target_id,
          },
        },
      });
    });
  }

  // Any Content Type can have labels
  paragraph.field_labels.forEach((label) => {
    // @ts-ignore
    filters.bool?.should?.push({
      term: {
        labels_tid: {
          value: label.target_id,
        },
      },
    });
  });

  return filters;
}

export function constructAdditionalSelectionFilters(
  contentSelection: ParagraphContentSelection,
): QueryDslQueryContainer {
  const contentType = contentSelection.field_content_type[0].value;
  const mode = contentSelection.field_selection_mode_highlighted[0].value;

  let filters: QueryDslQueryContainer = {
    bool: {
      should: [],
    },
  };

  switch (mode) {
    case 'highlighted':
      // @ts-ignore
      filters.bool?.should?.push({
        term: {
          promoted_to_frontpage: {
            value: true,
          },
        },
      });
      break;

    case 'advanced':
      filters = constructAdvancedFilters(
        filters,
        contentSelection,
      );
      break;

    case 'manual':
      if (contentType === 'article') {
        contentSelection.field_selected_articles.forEach((article) => {
          // @ts-ignore
          filters.bool?.should?.push({
            term: {
              nid: {
                value: article.target_id,
              },
            },
          });
        });
      }

      if (contentType === 'project') {
        contentSelection.field_selected_projects.forEach((project) => {
          // @ts-ignore
          filters.bool?.should?.push({
            term: {
              nid: {
                value: project.target_id,
              },
            },
          });
        });
      }

      if (contentType === 'profile') {
        contentSelection.field_selected_profiles.forEach((profile) => {
          // @ts-ignore
          filters.bool?.should?.push({
            term: {
              profile_id: {
                value: profile.target_id,
              },
            },
          });
        });
      }

      if (contentType === 'departments') {
        contentSelection.field_selected_departments.forEach((department) => {
          // @ts-ignore
          filters.bool?.should?.push({
            term: {
              tid: {
                value: department.target_id,
              },
            },
          });
        });
      }

      if (contentType === 'themes') {
        contentSelection.field_selected_themes.forEach((theme) => {
          // @ts-ignore
          filters.bool?.should?.push({
            term: {
              tid: {
                value: theme.target_id,
              },
            },
          });
        });
      }

      if (contentType === 'equipment') {
        contentSelection.field_selected_equipment.forEach((equipment) => {
          // @ts-ignore
          filters.bool?.should?.push({
            term: {
              nid: {
                value: equipment.target_id,
              },
            },
          });
        });
      }

      if (contentType === 'expertises') {
        contentSelection.field_selected_expertises.forEach((expertise) => {
          // @ts-ignore
          filters.bool?.should?.push({
            term: {
              tid: {
                value: expertise.target_id,
              },
            },
          });
        });
      }

      if (contentType === 'vacancy') {
        contentSelection.field_selected_vacancies.forEach((vacancy) => {
          // @ts-ignore
          filters.bool?.should?.push({
            term: {
              nid: {
                value: vacancy.target_id,
              },
            },
          });
        });
      }

      if (contentType === 'partner') {
        contentSelection.field_selected_partners.forEach((partner) => {
          // @ts-ignore
          filters.bool?.should?.push({
            term: {
              nid: {
                value: partner.target_id,
              },
            },
          });
        });
      }

      if (contentType === 'annual_report') {
        contentSelection.field_selected_annual_reports.forEach((annualReport) => {
          // @ts-ignore
          filters.bool?.should?.push({
            term: {
              nid: {
                value: annualReport.target_id,
              },
            },
          });
        });
      }

      break;

    default:
      break;
  }

  return filters;
}

export function constructAdditionalOverviewFilters(
  contentOverview: ParagraphContentOverview,
): QueryDslQueryContainer {
  const mode = contentOverview.field_selection_mode_overview[0].value;

  let filters: QueryDslQueryContainer = {
    bool: {
      should: [],
    },
  };

  switch (mode) {
    case 'advanced':
      filters = constructAdvancedFilters(
        filters,
        contentOverview,
      );
      break;

    case 'default':
      break;

    default:
      break;
  }

  return filters;
}

export function constructQuery(
  contentType: MetaParagraphsContentTypes,
  langcode: Langcodes,
): ElasticsearchQuery {
  let searchKey;

  switch (contentType) {
    case 'profile':
      searchKey = {
        profile_type: {
          value: 'default_profile',
        },
      };
      break;

    case 'departments':
    case 'themes':
    case 'expertises':
      searchKey = {
        vid: {
          value: contentType,
        },
      };
      break;

    default:
      searchKey = {
        content_type: {
          value: contentType,
        },
      };
      break;
  }

  return (
    {
      bool: {
        must: {
          term: searchKey,
        },
        filter: [
          {
            bool: {
              must:
                {
                  term: {
                    langcode,
                  },
                },
            },
          },
        ],
      },
    }
  );
}

export function constructSortingList(
  contentSelection: ParagraphContentSelection,
) {
  const contentType = contentSelection.field_content_type[0].value;

  let sortList;

  switch (contentType) {
    case 'article':
      sortList = contentSelection.field_selected_articles.map((article) => article.target_id);
      break;
    case 'project':
      sortList = contentSelection.field_selected_projects.map((project) => project.target_id);
      break;
    case 'vacancy':
      sortList = contentSelection.field_selected_vacancies.map((vacancy) => vacancy.target_id);
      break;
    case 'profile':
      sortList = contentSelection.field_selected_profiles.map((profile) => profile.target_id);
      break;
    case 'equipment':
      sortList = contentSelection.field_selected_equipment.map((equipment) => equipment.target_id);
      break;
    case 'departments':
      sortList = contentSelection.field_selected_departments.map((department) => department.target_id);
      break;
    case 'themes':
      sortList = contentSelection.field_selected_themes.map((theme) => theme.target_id);
      break;
    default:
      break;
  }

  return sortList;
}

export function sortByCustomOrder(
  hits: SearchAllContentHit[],
  sortingList: number[],
) {
  let key = 'nid';

  switch (hits[0]?.entity_type[0]) {
    case 'profile':
      key = 'profile_';
      break;
    case 'taxonomy_term':
      key = 'tid';
      break;
    default:
      break;
  }
  const result = hits.sort((a, b) => {
    const indexA = sortingList.indexOf(a[key][0]);
    const indexB = sortingList.indexOf(b[key][0]);

    if (indexA < indexB) return -1;
    if (indexA > indexB) return 1;
    return 0;
  });

  return result;
}
